import clsx from 'clsx';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { PlateElement, useEditorRef } from 'platejs/react';
import { useComboboxInput, useHTMLInputCursorState } from '@platejs/combobox/react';
import { SLASH_ITEMS, filterSlashItems } from './slashItems';
import type { SlashItem } from './slashItems';
import { usePlateEditorConfig } from '../../PlateEditorConfigContext';
import styles from './SlashInputElement.module.scss';

// The /-command combobox. SlashPlugin inserts a `slash_input` void inline node
// when the user types `/` at the start of a block (or after whitespace); this
// component renders for that node — a hidden-width query input plus a floating
// item list portaled to document.body.
//
// Architecture mirrors MentionInputElement exactly:
//   - useComboboxInput: handles blur/escape/backspace cancellation + removeInput
//   - useHTMLInputCursorState: feeds cursor position for backspace-at-start
// On select, removeInput removes the slash_input node then calls item.onSelect
// which runs the same transform the toolbar would run for that block type.

const DROPDOWN_WIDTH_PX = 220;
const DROPDOWN_MAX_HEIGHT_PX = 288;
const DROPDOWN_GAP_PX = 6;
const VIEWPORT_MARGIN_PX = 8;

interface DropdownPos {
    left: number;
    top?: number;
    bottom?: number;
}

function computePos(anchor: DOMRect): DropdownPos {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const maxLeft = vw - DROPDOWN_WIDTH_PX - VIEWPORT_MARGIN_PX;
    const left = Math.max(VIEWPORT_MARGIN_PX, Math.min(anchor.left, maxLeft));
    const spaceAbove = anchor.top;
    const spaceBelow = vh - anchor.bottom;
    if (spaceAbove >= DROPDOWN_MAX_HEIGHT_PX || spaceAbove >= spaceBelow) {
        return { left, bottom: vh - anchor.top + DROPDOWN_GAP_PX };
    }
    return { left, top: anchor.bottom + DROPDOWN_GAP_PX };
}

export function SlashInputElement(props: any) {
    const editor = useEditorRef();
    const { uploadSource } = usePlateEditorConfig();

    const inputRef = useRef<HTMLInputElement>(null);
    const wrapRef = useRef<HTMLSpanElement>(null);
    const [query, setQuery] = useState('');
    const [activeIndex, setActiveIndex] = useState(0);
    const [pos, setPos] = useState<DropdownPos | null>(null);

    const cursorState = useHTMLInputCursorState(inputRef);
    const { props: inputProps, removeInput } = useComboboxInput({
        ref: inputRef,
        cursorState,
        cancelInputOnBlur: true,
    });

    const filtered = useMemo(() => filterSlashItems(SLASH_ITEMS, query), [query]);

    useLayoutEffect(() => {
        const update = () => {
            const el = wrapRef.current;
            if (!el) return;
            setPos(computePos(el.getBoundingClientRect()));
        };
        update();
        window.addEventListener('scroll', update, true);
        window.addEventListener('resize', update);
        return () => {
            window.removeEventListener('scroll', update, true);
            window.removeEventListener('resize', update);
        };
    }, [query, filtered.length]);

    const selectItem = (item: SlashItem) => {
        removeInput(true);
        item.onSelect(editor, { uploadSource });
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        inputProps.onKeyDown(e);
        if (e.defaultPrevented) return;
        if (!filtered.length) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex((i) => (i + 1) % filtered.length);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex((i) => (i - 1 + filtered.length) % filtered.length);
        } else if (e.key === 'Enter' || e.key === 'Tab') {
            e.preventDefault();
            const item = filtered[Math.min(activeIndex, filtered.length - 1)];
            if (item) selectItem(item);
        }
    };

    return (
        <PlateElement {...props} as="span" attributes={{ ...props.attributes }}>
            <span ref={wrapRef} contentEditable={false} className={styles.Wrap}>
                <input
                    ref={inputRef}
                    className={styles.Input}
                    value={query}
                    aria-label="Slash command search"
                    onChange={(e) => {
                        setQuery(e.target.value);
                        setActiveIndex(0);
                    }}
                    onKeyDown={onKeyDown}
                    onBlur={inputProps.onBlur}
                />
            </span>
            {createPortal(
                <span
                    className={styles.Dropdown}
                    role="listbox"
                    style={
                        pos
                            ? { position: 'fixed', left: pos.left, top: pos.top, bottom: pos.bottom }
                            : { position: 'fixed', visibility: 'hidden' }
                    }
                >
                    {filtered.length === 0 ? (
                        <span className={styles.Empty}>No matching commands</span>
                    ) : (
                        filtered.map((item, i) => (
                            <span
                                key={item.id}
                                role="option"
                                aria-selected={i === activeIndex}
                                className={clsx(styles.Row, i === activeIndex && styles.RowActive)}
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    selectItem(item);
                                }}
                                onMouseEnter={() => setActiveIndex(i)}
                            >
                                <span className={styles.Label}>{item.label}</span>
                            </span>
                        ))
                    )}
                </span>,
                document.body,
            )}
            {props.children}
        </PlateElement>
    );
}
